import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import contractUserApi, { isLoggedIn } from '../../api/contractUserClient';
import ContractLayout from '../../components/ContractLayout';
import SignaturePad from './SignaturePad';
import './My.css';

const OTHER_Q1_TEXT = '"을"은 현재 전염병이나 기타 질환 병력 및 몸이 불편한 곳은 없습니까?';
const OTHER_Q2_TEXT = '"을"은 "갑" 이 공고한 취업규칙과 근무수칙 및 안전교육을 교육받고 인지하였습니까?';
const OTHER_Q3_TEXT = '"을"은 "갑"이 공고한 성희롱 예방교육을 교육받고 인지하였습니까?';

export default function MyContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [otherQ1, setOtherQ1] = useState(null);
  const [otherQ2, setOtherQ2] = useState(null);
  const [otherQ3, setOtherQ3] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [justSigned, setJustSigned] = useState(false);

  useEffect(() => { loadPreview(); }, [id]);

  async function loadPreview() {
    try {
      setLoading(true);
      const result = await contractUserApi.get(`/contracts/me/${id}/preview`);
      setData(result);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSign() {
    if (!signatureDataUrl) {
      alert('서명을 작성해 주세요.');
      return;
    }
    if (!agreed) {
      alert('근로계약서 내용에 동의해 주셔야 서명이 가능합니다.');
      return;
    }
    if (otherQ1 === null || otherQ2 === null || otherQ3 === null) {
      alert('기타사항의 모든 질문에 응답해 주세요.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await contractUserApi.post(`/contracts/me/${id}/sign`, { signatureDataUrl, otherQ1, otherQ2, otherQ3 });
      setJustSigned(true);
      await loadPreview();
    } catch (err) {
      setSubmitError(err.message || '서명 처리 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  }

  function handleDownload() {
    const url = contractUserApi.fileUrl(`/contracts/me/${id}/download`);
    window.open(url, '_blank');
  }

  if (!isLoggedIn()) return <Navigate to="/my/login" replace />;

  return (
    <div className="my-page">
      <header className="my-page__header">
        <div className="container my-page__header-inner">
          <button className="back-btn" onClick={() => navigate('/my/contracts')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
            목록으로
          </button>
        </div>
      </header>

      <main className="container my-page__content">
        {loading && <div className="state-message">불러오는 중입니다...</div>}
        {!loading && error && <div className="state-message state-message--error">{error}</div>}

        {!loading && data && (
          <>
            <h1>근로계약서</h1>

            <div className="contract-paper">
              <ContractLayout
                mode="view"
                form={data.contract}
                companyInfo={data.companyInfo}
                templateName={data.templateName}
              />
            </div>

            {data.contract.isSigned ? (
              <div className="contract-signed-box">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                <h3>서명이 완료된 계약서입니다.</h3>
                <p className="text-muted">서명일: {String(data.contract.signedAt).slice(0, 16).replace('T', ' ')}</p>
                <button className="btn btn-primary" onClick={handleDownload}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  PDF 다운로드
                </button>
              </div>
            ) : (
              <div className="contract-sign-box">
                <h3>서명하기</h3>
                <p className="text-muted" style={{ marginBottom: '1.25rem' }}>
                  위 근로계약서 내용을 확인하셨다면, 아래 사항에 응답하고 서명해 주세요.
                </p>

                {submitError && <div className="form-error">{submitError}</div>}

                {/* 제10조 기타사항 */}
                <div style={{
                  border: '1px solid var(--gray-200)',
                  borderRadius: 'var(--radius-md)',
                  padding: '1rem 1.25rem',
                  marginBottom: '1.25rem',
                  background: 'var(--blue-50)',
                }}>
                  <p style={{ fontWeight: 700, marginBottom: '0.75rem', fontSize: '0.9rem', color: 'var(--navy-800)' }}>
                    제10조 기타사항 <span style={{ color: '#e53e3e', fontSize: '0.8rem', fontWeight: 400 }}>(필수)</span>
                  </p>

                  <div style={{ marginBottom: '0.85rem' }}>
                    <p style={{ fontSize: '0.9rem', marginBottom: '0.4rem' }}>① {OTHER_Q1_TEXT}</p>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                        <input
                          type="radio"
                          name="otherQ1"
                          checked={otherQ1 === true}
                          onChange={() => setOtherQ1(true)}
                          style={{ width: 'auto' }}
                        />
                        예
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                        <input
                          type="radio"
                          name="otherQ1"
                          checked={otherQ1 === false}
                          onChange={() => setOtherQ1(false)}
                          style={{ width: 'auto' }}
                        />
                        아니오
                      </label>
                    </div>
                  </div>

                  <div style={{ marginBottom: '0.85rem' }}>
                    <p style={{ fontSize: '0.9rem', marginBottom: '0.4rem' }}>② {OTHER_Q2_TEXT}</p>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                        <input type="radio" name="otherQ2" checked={otherQ2 === true} onChange={() => setOtherQ2(true)} style={{ width: 'auto' }} />
                        예
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                        <input type="radio" name="otherQ2" checked={otherQ2 === false} onChange={() => setOtherQ2(false)} style={{ width: 'auto' }} />
                        아니오
                      </label>
                    </div>
                  </div>

                  <div>
                    <p style={{ fontSize: '0.9rem', marginBottom: '0.4rem' }}>③ {OTHER_Q3_TEXT}</p>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                        <input type="radio" name="otherQ3" checked={otherQ3 === true} onChange={() => setOtherQ3(true)} style={{ width: 'auto' }} />
                        예
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                        <input type="radio" name="otherQ3" checked={otherQ3 === false} onChange={() => setOtherQ3(false)} style={{ width: 'auto' }} />
                        아니오
                      </label>
                    </div>
                  </div>
                </div>

                <SignaturePad onChange={setSignatureDataUrl} />

                <label className="checkbox-row" style={{ marginTop: '1.5rem' }}>
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    style={{ width: 'auto' }}
                  />
                  <span>위 근로계약서 내용을 모두 확인하였으며, 이에 동의합니다. <span style={{ color: '#e53e3e', fontSize: '0.85em' }}>(필수)</span></span>
                </label>

                <button
                  className="btn btn-accent"
                  style={{ marginTop: '1.25rem', width: '100%', justifyContent: 'center' }}
                  onClick={handleSign}
                  disabled={submitting || otherQ1 === null || otherQ2 === null || otherQ3 === null || !agreed}
                >
                  {submitting ? '제출 중...' : '서명 완료하기'}
                </button>
              </div>
            )}

            {justSigned && (
              <div className="form-success" style={{ marginTop: '1rem' }}>
                서명이 정상적으로 제출되었습니다. 위 다운로드 버튼으로 서명된 계약서를 받아보실 수 있습니다.
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
